'use client';

import React from 'react';
import { Loader2, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  className?: string;
  text?: string;
  fullPage?: boolean;
  variant?: 'spinner' | 'pulse' | 'bot';
}

const sizeMap = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

const textSizeMap = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'text-blue-500',
  className = '',
  text,
  fullPage = false,
  variant = 'spinner',
}) => {
  const spinnerElement = (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      {variant === 'bot' ? (
        <div className="relative flex items-center justify-center">
          <div className={cn('absolute rounded-full animate-ping opacity-25 bg-blue-500', sizeMap[size])} />
          <div className={cn('relative rounded-xl bg-slate-900/90 border border-blue-500/30 p-2 text-blue-400 shadow-lg shadow-blue-500/10 flex items-center justify-center')}>
            <Bot className={sizeMap[size]} />
          </div>
        </div>
      ) : variant === 'pulse' ? (
        <div className="relative flex items-center justify-center">
          <div className={cn('rounded-full bg-blue-500/20 animate-pulse', sizeMap[size])} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping" />
          </div>
        </div>
      ) : (
        <Loader2 className={cn('animate-spin', sizeMap[size], color)} />
      )}

      {text && (
        <p className={cn('font-medium text-slate-300 animate-pulse tracking-wide', textSizeMap[size])}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md">
        <div className="glass-card p-8 rounded-2xl border border-slate-800/80 shadow-2xl max-w-sm w-full mx-4 text-center">
          {spinnerElement}
        </div>
      </div>
    );
  }

  return spinnerElement;
};

export default LoadingSpinner;
